'use client';

import { useCallback } from 'react';
import styled from 'styled-components';
import { LabsChip } from '@frameio/shared/components/LabsChip';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import {
  MenuButton,
  MenuGroup,
  MenuNonInteractiveArea,
  Separator,
  theme,
} from '@frameio/vapor';
import {
  IconCheckmark,
  IconInfo,
  IconPaintPalette,
} from '@frameio/vapor-icons';
import {
  DEFAULT_FOLDER_COLOR,
  FOLDER_COLOR_OPTIONS,
  type FolderColorKey,
} from '../constants/folderColors';
import { useFolderColor } from '../hooks/useFolderColor';
import TEXT from './generated/TEXT/FolderColorMenuItems';

type Props = {
  folderIds: string[];
  projectId: string;
};

TEXT.DEFINE({
  /** Label for the color submenu button */
  colorMenuLabel: 'Folder Color',
  /** Label for the default (no color) option */
  defaultColor: 'Default',
  /** Disclaimer text shown at the bottom of the color submenu */
  disclaimer:
    'This feature is in early development and may change or be discontinued.',
});

export function FolderColorMenuItems({ folderIds, projectId }: Props) {
  const isSingle = folderIds.length === 1;
  const { colorKey, setColor: setFolderColor } = useFolderColor(folderIds);
  const track = useTrackUnsafe();

  // Only show a checkmark for single-folder selection; multi-select shows no active state.
  const currentKey = isSingle ? colorKey : undefined;

  const setColor = useCallback(
    (key: FolderColorKey | null) => {
      track('folder-color-changed', {
        asset_ids: folderIds,
        project_id: projectId,
        selected_color: key ?? 'default',
      });
      setFolderColor(key);
    },
    [folderIds, projectId, setFolderColor, track]
  );

  const submenu = (
    <SubmenuContainer>
      <MenuGroup>
        <MenuButton
          iconAfter={
            currentKey === null ? (
              <IconCheckmark color={theme.colors.brand.primary} size={16} />
            ) : undefined
          }
          iconBefore={
            <ColorCircleWrapper>
              <ColorCircle $color={DEFAULT_FOLDER_COLOR} />
            </ColorCircleWrapper>
          }
          onPress={() => setColor(null)}
        >
          {TEXT.defaultColor}
        </MenuButton>
        {FOLDER_COLOR_OPTIONS.map(({ key, token, label }) => (
          <MenuButton
            key={key}
            iconAfter={
              currentKey === key ? (
                <IconCheckmark color={theme.colors.brand.primary} size={16} />
              ) : undefined
            }
            iconBefore={
              <ColorCircleWrapper>
                <ColorCircle $color={token} />
              </ColorCircleWrapper>
            }
            onPress={() => setColor(key)}
          >
            {label}
          </MenuButton>
        ))}
      </MenuGroup>
      <Separator color="secondary" gap={0} />
      <MenuNonInteractiveArea icon={<IconInfo size={16} />}>
        {TEXT.disclaimer}
      </MenuNonInteractiveArea>
    </SubmenuContainer>
  );

  return (
    <MenuButton
      iconAfter={<LabsChip />}
      iconBefore={<IconPaintPalette />}
      submenu={submenu}
    >
      {TEXT.colorMenuLabel}
    </MenuButton>
  );
}

const SubmenuContainer = styled.div`
  width: 240px;
`;

const ColorCircleWrapper = styled.div`
  align-items: center;
  display: flex;
  padding-left: var(--spaces-4);
`;

const ColorCircle = styled.div<{ $color: string }>`
  background-color: ${(p) => p.$color};
  border-radius: 50%;
  box-shadow: 0 0 0 1px var(--colors-border-primary);
  flex-shrink: 0;
  height: 16px;
  margin-right: var(--spaces-8);
  width: 16px;
`;
